import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { EduShell, EmptyState, StatusBadge, STUDENT_NAV } from "@/components/EduShell";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { eduRpc, formatDue, isOverdue } from "@/lib/education";
import { AssignmentAiPanel } from "@/components/AssignmentAiPanel";
import { DEFAULT_AI_POLICY, normalisePolicy, type AiPolicy } from "@/lib/ai-policy";

import { T } from "@/components/T";
import i18n from "@/i18n";
export const Route = createFileRoute("/assignments/$assignmentId")({
  component: AssignmentWorkspace,
  head: () => ({
    meta: [
      { title: "Assignment - Inquira" },
      { name: "description", content: "Work through your assignment, save drafts, submit and read your teacher's feedback." },
      { property: "og:title", content: "Work on your assignment" },
      { property: "og:description", content: "Instructions, questions, drafts and feedback in one workspace." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
});

interface Workspace {
  assignment: {
    id: string;
    title: string;
    instructions: string | null;
    kind: string | null;
    due_at: string | null;
    points: number | null;
    ai_mode: string | null;
    allow_late: boolean | null;
    class_name: string | null;
  } | null;
  questions: Array<{ id: string; prompt: string; points: number | null; position: number | null }>;
  submission: {
    id: string | null;
    status: string;
    note: string | null;
    answers: Array<{ question_id?: string; answer?: string }> | null;
    score: number | null;
    feedback: string | null;
    submitted_at: string | null;
  } | null;
}

function AssignmentWorkspace() {
  const { assignmentId } = useParams({ from: "/assignments/$assignmentId" });
  const { user } = useAuth();
  const [ws, setWs] = useState<Workspace | null>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [policy, setPolicy] = useState<AiPolicy>({ ...DEFAULT_AI_POLICY });

  const load = useCallback(async () => {
    const { data, error } = await eduRpc<Workspace>("edu_assignment_workspace", { _assignment_id: assignmentId });
    if (error) { toast.error(error); return; }
    setWs(data);
    setNote(data?.submission?.note ?? "");
    const map: Record<string, string> = {};
    for (const a of data?.submission?.answers ?? []) {
      if (a?.question_id) map[a.question_id] = a.answer ?? "";
    }
    setAnswers(map);
    // The effective policy is always resolved on the server: assignment first,
    // then the classroom default, then the platform default.
    const { data: raw } = await eduRpc<unknown>("edu_effective_ai_policy", { _assignment_id: assignmentId });
    const resolved = normalisePolicy(raw);
    setPolicy(resolved);
    const status = data?.submission?.status ?? "not_started";
    if (resolved.assessment_mode && !["submitted", "graded", "returned", "late"].includes(status)) {
      await eduRpc("edu_start_assessment", { _assignment_id: assignmentId });
    }
  }, [assignmentId]);

  useEffect(() => { if (user) void load(); }, [user, load]);

  async function save(submit: boolean) {
    setBusy(true);
    const { error } = await eduRpc("edu_save_submission", {
      _assignment_id: assignmentId,
      _note: note || null,
      _answers: (ws?.questions ?? []).map((q) => ({ question_id: q.id, answer: answers[q.id] ?? "" })),
      _submit: submit,
    });
    setBusy(false);
    if (error) { toast.error(error); return; }
    toast.success(submit ? "Submitted to your teacher" : "Draft saved");
    void load();
  }

  if (!user) {
    return (
      <EduShell nav={STUDENT_NAV} title={i18n.t("edu.assignments_assignmentId.assignment")}>
        <EmptyState title={i18n.t("edu.assignments_assignmentId.sign_in_first")} body="Sign in to open this assignment." />
        <div className="mt-4">
          <Link to="/login" className="rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground"><T k="edu.assignments_assignmentId.sign_in" /></Link>
        </div>
      </EduShell>
    );
  }

  const a = ws?.assignment;
  const sub = ws?.submission;
  const locked = sub && ["submitted", "graded", "returned", "late"].includes(sub.status) && sub.status !== "returned";

  return (
    <EduShell
      nav={STUDENT_NAV}
      title={a?.title ?? "Assignment"}
      subtitle={a ? [a.class_name, a.kind, formatDue(a.due_at)].filter(Boolean).join(" · ") : undefined}
      actions={sub ? <StatusBadge status={sub.status} /> : undefined}
    >
      {a && (
        <AssignmentAiPanel
          assignmentId={assignmentId}
          policy={policy}
          questions={(ws?.questions ?? []).map((q) => ({ id: q.id, prompt: q.prompt }))}
          submitted={!!locked || sub?.status === "graded" || sub?.status === "returned"}
        />
      )}

      {isOverdue(a?.due_at, sub?.status) && (
        <p className="mb-4 rounded-2xl border border-destructive/40 bg-destructive/10 p-3 text-sm font-semibold text-destructive">
          This is past the due date{a?.allow_late ? " — late work is still accepted." : "."}
        </p>
      )}

      {a?.instructions && (
        <div className="mb-6 whitespace-pre-wrap rounded-2xl border border-rule bg-card p-4 text-sm text-ink">
          {a.instructions}
        </div>
      )}

      {sub?.feedback && (
        <div className="mb-6 rounded-2xl border border-sky-500/40 bg-sky-500/10 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-ink-muted"><T k="edu.assignments_assignmentId.teacher_feedback" /></p>
          <p className="mt-1 text-sm text-ink">{sub.feedback}</p>
          {sub.score !== null && (
            <p className="mt-2 font-display text-lg font-black text-ink">
              {sub.score}{a?.points ? ` / ${a.points}` : ""}
            </p>
          )}
        </div>
      )}

      <div className="space-y-4">
        {(ws?.questions ?? []).map((q, i) => (
          <div key={q.id} className="rounded-2xl border border-rule bg-card p-4">
            <p className="font-semibold text-ink">
              {i + 1}. {q.prompt}
              {q.points ? <span className="ml-2 text-xs text-ink-muted">{q.points} pts</span> : null}
            </p>
            <textarea
              className="mt-2 w-full rounded-xl border border-rule bg-background px-3 py-2 text-sm text-ink"
              rows={3}
              disabled={!!locked}
              value={answers[q.id] ?? ""}
              onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })}
            />
          </div>
        ))}

        <div className="rounded-2xl border border-rule bg-card p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-ink-muted"><T k="edu.assignments_assignmentId.your_work_notes" /></p>
          <textarea
            className="mt-2 w-full rounded-xl border border-rule bg-background px-3 py-2 text-sm text-ink"
            rows={6}
            disabled={!!locked}
            placeholder={i18n.t("edu.assignments_assignmentId.type_your_answer_or_explain_your_steps")}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>

        {locked ? (
          <p className="text-sm text-ink-muted">
            Submitted{sub?.submitted_at ? ` on ${new Date(sub.submitted_at).toLocaleString()}` : ""}. Your teacher can reopen it.
          </p>
        ) : (
          <div className="flex gap-2">
            <Button onClick={() => save(true)} disabled={busy}><T k="edu.assignments_assignmentId.submit" /></Button>
            <Button variant="outline" onClick={() => save(false)} disabled={busy}><T k="edu.assignments_assignmentId.save_draft" /></Button>
          </div>
        )}
      </div>
    </EduShell>
  );
}
